import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import { AuthContext } from "../features/auth/context/AuthContext";
import { getAuthenticatedAxios } from "../utils/api";

const UsageContext = createContext();

const EMPTY_USAGE_DATA = {
  current_usage: 0,
  limit: 0,
  reset_date: null,
  next_reset_date: null,
  percentage_used: 0,
};

const getAuthUserId = (user) => user?.id || user?.user_id || user?.UserId || null;

export const useUsage = () => {
  const context = useContext(UsageContext);
  if (!context) {
    throw new Error("useUsage must be used within a UsageProvider");
  }
  return context;
};

export const UsageProvider = ({ children }) => {
  const { user, isAuthenticated, isLoading: isAuthLoading } =
    useContext(AuthContext);
  const authUserId = getAuthUserId(user);
  const authUserIdRef = useRef(authUserId);
  const [usageData, setUsageData] = useState(EMPTY_USAGE_DATA);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSubmittedSurvey, setHasSubmittedSurvey] = useState(false);

  // Fetch current usage
  const fetchUsage = useCallback(async () => {
    const expectedUserId = authUserIdRef.current;
    if (!expectedUserId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const axios = getAuthenticatedAxios();
      const response = await axios.get("/api/v1/usage/current");
      if (response.data.success && authUserIdRef.current === expectedUserId) {
        setUsageData({
          ...EMPTY_USAGE_DATA,
          ...response.data.data,
        });
      }
    } catch (err) {
      if (authUserIdRef.current === expectedUserId) {
        console.error("Failed to fetch usage:", err);
        setError(err.message);
      }
    } finally {
      if (authUserIdRef.current === expectedUserId) {
        setLoading(false);
      }
    }
  }, []);

  // Check if user has submitted survey this month
  const checkSurveyStatus = useCallback(async () => {
    const expectedUserId = authUserIdRef.current;
    if (!expectedUserId) {
      return;
    }

    try {
      const axios = getAuthenticatedAxios();
      const response = await axios.get("/api/v1/usage/check-survey");
      if (response.data.success && authUserIdRef.current === expectedUserId) {
        setHasSubmittedSurvey(response.data.has_responded);
      }
    } catch (err) {
      if (authUserIdRef.current === expectedUserId) {
        console.error("Failed to check survey status:", err);
      }
    }
  }, []);

  // Submit survey response
  const submitSurvey = useCallback(async (surveyData) => {
    try {
      const axios = getAuthenticatedAxios();
      const response = await axios.post("/api/v1/usage/survey", surveyData);
      if (response.data.success) {
        setHasSubmittedSurvey(true);
        return { success: true, message: response.data.message };
      }
    } catch (err) {
      console.error("Failed to submit survey:", err);
      return { success: false, error: err.message };
    }
  }, []);

  // Increment usage (called after successful search)
  const incrementUsage = useCallback(() => {
    setUsageData((prev) => ({
      ...prev,
      current_usage: prev.current_usage + 1,
      percentage_used:
        prev.limit > 0 ? ((prev.current_usage + 1) / prev.limit) * 100 : 0,
    }));
  }, []);

  // Update usage from WebSocket message
  const updateUsage = useCallback((current, limit) => {
    setUsageData((prevData) => ({
      ...prevData,
      current_usage: current,
      limit: typeof limit === "number" ? limit : prevData.limit,
      percentage_used:
        (typeof limit === "number" ? limit : prevData.limit) > 0
          ? (current / (typeof limit === "number" ? limit : prevData.limit)) *
            100
          : 0,
      // Keep existing reset metadata
      reset_date: prevData.reset_date,
      next_reset_date: prevData.next_reset_date,
    }));
  }, []);

  // Check if limit is reached
  const isLimitReached =
    usageData.limit > 0 && usageData.current_usage >= usageData.limit;

  useLayoutEffect(() => {
    authUserIdRef.current = authUserId;
    setUsageData(EMPTY_USAGE_DATA);
    setError(null);
    setHasSubmittedSurvey(false);

    if (!isAuthenticated || !authUserId) {
      setLoading(false);
    }
  }, [authUserId, isAuthenticated]);

  // Fetch usage on mount and when user logs in
  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (isAuthenticated && authUserId) {
      fetchUsage();
      checkSurveyStatus();
    }
  }, [authUserId, isAuthenticated, isAuthLoading, fetchUsage, checkSurveyStatus]);

  const value = {
    usageData,
    loading,
    error,
    hasSubmittedSurvey,
    isLimitReached,
    fetchUsage,
    checkSurveyStatus,
    submitSurvey,
    incrementUsage,
    updateUsage,
  };

  return (
    <UsageContext.Provider value={value}>{children}</UsageContext.Provider>
  );
};
